import React, { useState, useMemo, useCallback } from "react";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useModal } from "hooks/useModal";
import {
  useGetBillingInvoicesQuery,
  useDeleteBillingInvoiceMutation,
  useBulkAcceptInvoicesMutation,
  useBulkRejectInvoicesMutation,
  useBulkAcceptInvoicesAdminMutation,
  useBulkRejectInvoicesAdminMutation,
  useUpdateBillingInvoiceMutation,
} from "store/apis/billingInvoice";
import { BillingInvoice } from "types/Models/billing";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  RowSelectionState,
  Row,
} from "react-table-8.10.7";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Modal from "components/DesignSystem/Modal/Modal";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { TableUI } from "components/design/TableUI";
import Table from "components/DesignSystem/Table/V2/Table";
import { BackendError } from "types/utils/error";
import { CheckOutline } from "components/icons/CheckOutline";
import { CrossOutlined } from "components/icons/CrossOutlined";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import {
  ACCOUNTING_METHODS_MAP,
  CRM_SUBSCRIPTION_BADGE_MAP,
  CRM_SUBSCRIPTION_TYPE,
  CrmSubscriptionType,
} from "dictionaries";
import { DividerLine } from "components/icons/DividerLine";
import { Form, Formik } from "formik";
import { Info } from "components/icons/Info";
import { formatDate } from "utils/formatDate";
import { Link } from "components/DesignSystem/Link/Link";
import { PriceInput } from "components/PriceInput/PriceInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { stopPropagation } from "utils/stopPropagation";
import classNames from "classnames";
import { draftInvoiceValidationSchema } from "./draftInvoiceValidationSchema";
import { US_BOOKKEEPING_SUBSCRIPTION_SUB_TASK } from "types/Models/services";
import { ConditionalLink } from "components/conditionalLink";
import { Badge } from "components/design/badge";

interface DraftInvoicesProps {
  invoices: BillingInvoice[];
  showCompanyInfo?: boolean;
}

type TableMetaType = {
  showCompanyInfo?: boolean;
};

const ApproveBody = ({
  invoice,
  isLoading,
  onSubmit,
}: {
  invoice: BillingInvoice;
  isLoading: boolean;
  onSubmit: () => void;
}) => (
  <>
    <Modal.Header>
      <Modal.Title>Approve invoice</Modal.Title>
      <Modal.Close />
    </Modal.Header>
    <Modal.Body>
      <p className="t-mb-4">
        Are you sure you want to approve the draft invoice for the{" "}
        <strong>{invoice?.short_name}</strong>?
      </p>
      <p className="t-text-body-sm t-text-text-30 t-flex t-items-center t-gap-2">
        <Info stroke="1.4" size="10" />
        Upon approval, the invoice will be sent to the customer via email.
      </p>
    </Modal.Body>
    <Modal.FooterButtonGroup>
      <Modal.RawClose asChild>
        <Button type="button" customType="secondary">
          Cancel
        </Button>
      </Modal.RawClose>
      <Button
        type="button"
        onClick={onSubmit}
        customType="primary"
        isLoading={isLoading}
        disabled={isLoading}
      >
        Approve
      </Button>
    </Modal.FooterButtonGroup>
  </>
);

const RejectBody = ({
  invoice,
  isLoading,
  onSubmit,
}: {
  invoice: BillingInvoice;
  isLoading: boolean;
  onSubmit: () => void;
}) => (
  <>
    <Modal.Header>
      <Modal.Title>Reject invoice</Modal.Title>
      <Modal.Close />
    </Modal.Header>
    <Modal.Body>
      <p className="t-mb-4">
        Are you sure you want to reject the draft invoice raised for the{" "}
        <strong>{invoice?.short_name}</strong>?
      </p>
      <p className="t-text-sm t-text-text-60">This action is irreversible.</p>
    </Modal.Body>
    <Modal.FooterButtonGroup>
      <Modal.RawClose asChild>
        <Button type="button" customType="secondary">
          Cancel
        </Button>
      </Modal.RawClose>
      <Button
        type="button"
        customType="danger"
        onClick={onSubmit}
        isLoading={isLoading}
        disabled={isLoading}
      >
        Reject
      </Button>
    </Modal.FooterButtonGroup>
  </>
);

const CalculatedAmount = ({
  invoice,
  align = "left",
}: {
  invoice: BillingInvoice;
  align?: "right" | "left";
}) => {
  const { monthly, active_subscription, monthly_plus, catchup_task } =
    invoice.task?.calculated_amount || {};

  if (active_subscription) {
    return (
      <div
        className={classNames({
          "t-text-right": align === "right",
          "t-text-left": align === "left",
        })}
      >
        <AmountSuperScript amount={Number(active_subscription.amount || 0)} />
        <div className="t-text-caption-italic t-italic t-text-text-30">
          Based on {active_subscription.name.replace("Bookkeeping ", "")}
        </div>
      </div>
    );
  }

  if (monthly || monthly_plus) {
    return (
      <span
        className={classNames("t-flex t-flex-col t-gap-1", {
          "t-justify-end": align === "right",
        })}
      >
        <span
          className={classNames("t-flex t-gap-1", {
            "t-justify-end": align === "right",
          })}
        >
          <span className="t-text-text-30 t-text-body-sm">
            {monthly?.name?.replace("Bookkeeping ", "")}:{" "}
          </span>{" "}
          <span>
            <AmountSuperScript amount={Number(monthly?.amount || 0)} />
          </span>
        </span>
        {monthly_plus && (
          <>
            <span
              className={classNames("t-flex  t-gap-1", {
                "t-justify-end": align === "right",
              })}
            >
              <span className="t-text-text-30 t-text-body-sm">
                {monthly_plus?.name?.replace("Bookkeeping ", "")}:{" "}
              </span>
              <span>
                <AmountSuperScript amount={Number(monthly_plus?.amount || 0)} />
              </span>
            </span>
          </>
        )}
      </span>
    );
  }

  return (
    <div
      className={classNames({
        "t-text-left": align === "left",
        "t-text-right": align === "right",
      })}
    >
      <AmountSuperScript amount={Number(catchup_task?.amount || 0)} />
    </div>
  );
};

const InvoiceAction = ({ invoice }: { invoice: BillingInvoice }) => {
  const approveModal = useModal();
  const rejectModal = useModal();
  const { successToast, alertToast } = useToast();
  const [acceptInvoice, { isLoading: isAccepting }] =
    useBulkAcceptInvoicesAdminMutation();
  const [deleteInvoice, { isLoading: isRejecting }] =
    useDeleteBillingInvoiceMutation();

  const handleAcceptInvoice = async (invoiceId: string) => {
    try {
      await acceptInvoice({
        invoice_ids: [invoiceId],
      }).unwrap();
      successToast({ message: "Invoice accepted successfully" });
      approveModal.close();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Failed to accept invoice",
      });
    }
  };

  const handleRejectInvoice = async (invoice: BillingInvoice) => {
    try {
      await deleteInvoice({
        groupId: invoice.company_group.uuid,
        entityId: invoice.company_entity.uuid,
        invoiceId: invoice.uuid,
      }).unwrap();
      successToast({ message: "Invoice rejected successfully" });
      rejectModal.close();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Failed to reject invoice",
      });
    }
  };

  return (
    <div className="t-flex t-gap-2 t-justify-center">
      <Modal.Root open={approveModal.isOpen} onOpenChange={approveModal.toggle}>
        <Modal.Trigger asChild>
          <Button
            size="xsmall"
            customType="icon"
            onClick={(e) => {
              if (isNaN(Number(invoice.amount))) {
                e.preventDefault();
                return;
              }
              e.stopPropagation();
            }}
          >
            <span className="t-text-text-30">
              <CheckOutline />
            </span>
          </Button>
        </Modal.Trigger>
        <Modal.Content size="regular" onClick={stopPropagation}>
          <ApproveBody
            onSubmit={() => {
              handleAcceptInvoice(invoice.uuid);
            }}
            isLoading={isAccepting}
            invoice={invoice}
          />
        </Modal.Content>
      </Modal.Root>{" "}
      <Modal.Root open={rejectModal.isOpen} onOpenChange={rejectModal.toggle}>
        <Modal.Trigger asChild>
          <Button size="xsmall" customType="icon" onClick={stopPropagation}>
            <span className="t-text-text-30">
              <CrossOutlined stroke="1.4" size="16" />
            </span>
          </Button>
        </Modal.Trigger>
        <Modal.Content size="regular" onClick={stopPropagation}>
          <RejectBody
            invoice={invoice}
            onSubmit={() => {
              handleRejectInvoice(invoice);
            }}
            isLoading={isRejecting}
          />
        </Modal.Content>
      </Modal.Root>
    </div>
  );
};

const columnHelper = createColumnHelper<BillingInvoice>();

const columns = [
  columnHelper.display({
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
        onClick={stopPropagation}
        disabled={table.options.data.some((o) => isNaN(Number(o.amount)))}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect() || isNaN(Number(row.original.amount))}
        onChange={row.getToggleSelectedHandler()}
        onClick={stopPropagation}
      />
    ),
    size: 2,
  }),
  columnHelper.accessor("company_group.name", {
    header: "GROUP/ENTITY",
    size: 15,
    cell: (info) => {
      const meta = info.table.options.meta as TableMetaType;
      if (!meta.showCompanyInfo) return null;

      const rowData = info.row.original;
      return (
        <div className="t-flex t-gap-2 t-items-center">
          <div className="t-flex t-flex-col t-gap-1 t-text-body-sm ">
            <Link to={`/crm/${rowData.company_group.uuid}`}>
              {info.getValue()}
            </Link>

            {rowData.company_entity && (
              <span className="t-text-body-sm t-text-text-60">
                {rowData.company_entity.registered_name}
              </span>
            )}
          </div>
          {Boolean(rowData?.subscription_name) &&
            rowData?.subscription_name !== "NOT_STARTED" && (
              <Badge
                size="small"
                color={
                  CRM_SUBSCRIPTION_TYPE[
                    rowData?.subscription_name as CrmSubscriptionType
                  ]
                }
              >
                {
                  CRM_SUBSCRIPTION_BADGE_MAP[
                    rowData?.subscription_name as CrmSubscriptionType
                  ]
                }
              </Badge>
            )}
        </div>
      );
    },
  }),
  columnHelper.accessor("spoc", {
    header: "SPOC",
    size: 10,
    cell: (info) => {
      const meta = info.table.options.meta as TableMetaType;
      if (!meta.showCompanyInfo) return null;

      const spoc = info.getValue();
      if (!spoc) return <span className="t-text-text-30">—</span>;
      return <span>{spoc.name}</span>;
    },
  }),
  columnHelper.accessor("short_name", {
    header: "ITEM",
    size: 41,
    cell: (info) => (
      <div className="t-flex t-flex-col t-gap-1">
        <p className="t-text-purple t-m-0">{info.getValue()}</p>
        <div className="t-flex t-gap-2 t-text-body-sm t-text-text-30 t-items-center">
          {info.row.original.task?.accounting_method && (
            <>
              <span className="t-capitalize ">
                {
                  ACCOUNTING_METHODS_MAP[
                    info.row.original.task?.accounting_method
                  ]
                }
              </span>
              <span className="t-text-neutral-20">
                <DividerLine />
              </span>
            </>
          )}
          {info.row.original.task?.no_of_transactions &&
            info.row.original.task.base_task_key !==
              US_BOOKKEEPING_SUBSCRIPTION_SUB_TASK && (
              <>
                <span className="">
                  {info.row.original.task.no_of_transactions} transactions
                </span>
                <span className="t-text-neutral-20">
                  <DividerLine />
                </span>
              </>
            )}
          <span className="">
            Monthly billable expence vol. :{" "}
            <AmountSuperScript
              amount={
                info.row.original.task?.monthly_billable_expenses_volume || 0
              }
            />
          </span>
        </div>
      </div>
    ),
  }),
  columnHelper.display({
    id: "amount_paid",
    header: () => <p className="t-text-right t-m-0">AMOUNT PAID</p>,
    size: 10,
    cell: ({ row }) => (
      <div className="t-text-right">
        {row.original.task?.linked_invoice_paid_amount ||
        row.original.task?.linked_invoice_paid_amount === 0 ? (
          <AmountSuperScript
            amount={Number(row.original.task?.linked_invoice_paid_amount || 0)}
          />
        ) : (
          <span>None</span>
        )}
      </div>
    ),
  }),
  columnHelper.display({
    id: "calculated_amount",
    header: () => <p className="t-text-right t-m-0">CALCULATED AMOUNT</p>,
    size: 15,
    cell: ({ row }) => (
      <CalculatedAmount align="right" invoice={row.original} />
    ),
  }),
  columnHelper.accessor("amount", {
    header: () => <p className="t-text-right t-m-0">INVOICE AMOUNT</p>,
    size: 15,
    cell: (info) => (
      <div className="t-text-right">
        <div className="t-text-right">
          {isNaN(Number(info.getValue())) ? (
            <span>TBD</span>
          ) : (
            <AmountSuperScript amount={Number(info.getValue() || 0)} />
          )}
          {info.row.original?.task?.calculated_amount?.active_subscription && (
            <div className="t-text-caption-italic t-italic t-text-text-30">
              Based on current retainer
            </div>
          )}
        </div>
      </div>
    ),
  }),
  columnHelper.display({
    id: "actions",
    header: () => <p className="t-m-0 t-text-center">ACTION</p>,
    size: 2,
    cell: ({ row }) => <InvoiceAction invoice={row.original} />,
  }),
];

export const DraftInvoices = ({
  invoices,
  showCompanyInfo,
}: DraftInvoicesProps) => {
  const { successToast, alertToast } = useToast();
  const invoiceModal = useModal();
  const approveModal = useModal();
  const rejectModal = useModal();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [selectedInvoice, setSelectedInvoice] = useState<BillingInvoice | null>(
    null
  );

  const [deleteInvoice, { isLoading: isRejecting }] =
    useDeleteBillingInvoiceMutation();
  const [updateInvoice, { isLoading: isAccepting }] =
    useUpdateBillingInvoiceMutation();
  const [bulkRejectInvoicesAdmin, { isLoading: isBulkRejecting }] =
    useBulkRejectInvoicesAdminMutation();
  const [bulkAcceptInvoicesAdmin, { isLoading: isBulkAccepting }] =
    useBulkAcceptInvoicesAdminMutation();

  // Get selected invoice IDs from react-table's row selection state
  const selectedInvoices = useMemo(
    () => Object.keys(rowSelection).filter((key) => rowSelection[key]) || [],
    [rowSelection]
  );

  const handleRejectInvoice = useCallback(
    async (invoice: BillingInvoice) => {
      try {
        await deleteInvoice({
          groupId: invoice.company_group.uuid,
          entityId: invoice.company_entity.uuid,
          invoiceId: invoice.uuid,
        }).unwrap();
        successToast({ message: "Invoice rejected successfully" });
        rejectModal.close();
      } catch (error) {
        alertToast({
          message:
            (error as BackendError)?.data?.error?.message ||
            "Failed to reject invoice",
        });
      }
    },
    [deleteInvoice, successToast, alertToast]
  );

  const handleBulkAccept = async () => {
    try {
      await bulkAcceptInvoicesAdmin({
        invoice_ids: selectedInvoices,
      }).unwrap();
      successToast({ message: "Invoices accepted successfully" });
      setRowSelection({});
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Failed to accept invoices",
      });
    }
  };

  const handleBulkReject = async () => {
    try {
      await bulkRejectInvoicesAdmin({
        invoice_ids: selectedInvoices,
      }).unwrap();
      successToast({ message: "Invoices rejected successfully" });
      setRowSelection({});
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Failed to reject invoices",
      });
    }
  };

  const handleRowClick = (row: Row<BillingInvoice>) => {
    setSelectedInvoice(row.original);
    invoiceModal.open();
  };

  const handleAcceptFromModal = async (values: {
    task_short_name: string;
    invoice_amount: string | number;
  }) => {
    if (!selectedInvoice) return;

    try {
      await updateInvoice({
        groupId: selectedInvoice.company_group.uuid,
        entityId: selectedInvoice.company_entity.uuid,
        invoice_id: selectedInvoice.uuid,
        task_id: selectedInvoice.task.uuid,
        task_short_name: values.task_short_name,
        invoice_amount: String(values.invoice_amount),
        invoice_type: selectedInvoice.invoice_type!,
        status: "PENDING",
        season: selectedInvoice.season!,
      }).unwrap();
      successToast({ message: "Invoice updated successfully" });
      invoiceModal.close();
      setSelectedInvoice(null);
      rejectModal.close();
      approveModal.close();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Failed to update invoice",
      });
    }
  };

  const table = useReactTable({
    data: invoices,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    enableMultiSort: false,
    enableSortingRemoval: false,
    enableRowSelection: true,
    getRowId: (row) => row.uuid,
    onRowSelectionChange: setRowSelection,
    meta: {
      showCompanyInfo,
    },
    state: {
      sorting: [{ id: "created_at", desc: true }],
      rowSelection,
    },
    defaultColumn: {
      minSize: 2,
    },
  });

  if (invoices.length === 0) {
    return (
      <div className="t-text-center t-py-8">
        <p>No draft invoices found</p>
      </div>
    );
  }

  return (
    <div className="t-flex t-flex-col t-gap-4 t-text-text-60 t-h-full">
      {selectedInvoices.length > 0 && (
        <div className="t-flex t-gap-2 t-justify-end">
          <Button
            size="small"
            customType="primary_outlined"
            onClick={handleBulkAccept}
            isLoading={isBulkAccepting}
            disabled={isBulkAccepting || isBulkRejecting}
          >
            Bulk Accept ({selectedInvoices.length})
          </Button>
          <Button
            size="small"
            customType="primary_outlined"
            onClick={handleBulkReject}
            isLoading={isBulkRejecting}
            disabled={isBulkAccepting || isBulkRejecting}
          >
            Bulk Reject ({selectedInvoices.length})
          </Button>
        </div>
      )}

      <TableUI table={table} onRowClick={handleRowClick} />

      {/* Draft Invoice Details Modal */}
      {selectedInvoice && (
        <Modal.Root
          open={invoiceModal.isOpen}
          onOpenChange={invoiceModal.toggle}
        >
          <Modal.Content size="large" asChild>
            <FormikForm
              initialValues={{
                task_short_name: selectedInvoice.short_name || "",
                invoice_amount: isNaN(Number(selectedInvoice.amount))
                  ? 0
                  : Number(selectedInvoice.amount),
                group_id: selectedInvoice.company_group.uuid,
                entity_id: selectedInvoice.company_entity.uuid,
              }}
              validationSchema={draftInvoiceValidationSchema}
              // @ts-ignore
              onSubmit={handleAcceptFromModal}
            >
              {({ setFieldValue, submitForm, validateForm }) => (
                <>
                  <Modal.Header>
                    <Modal.Title>{selectedInvoice.short_name}</Modal.Title>
                    <Modal.Close />
                  </Modal.Header>
                  <Modal.Body className="t-text-text-60">
                    <p className="t-mb-4 t-text-text-30 t-text-body-sm">
                      Verify the details and make necessary changes to approve
                      or reject the draft.
                    </p>

                    <Accordion.Root
                      type="multiple"
                      defaultValue={[
                        "company-details",
                        "service-details",
                        "invoice-details",
                      ]}
                      className="t-flex t-flex-col t-gap-4"
                    >
                      {showCompanyInfo && (
                        <Accordion.Item value="company-details">
                          <Accordion.Trigger className="t-text-text-100">
                            Company Details
                          </Accordion.Trigger>
                          <Accordion.Content>
                            <div className="t-grid t-grid-cols-2 t-gap-4">
                              <InfoItem label="Group">
                                <ConditionalLink
                                  to={`/admin/crm/${selectedInvoice.company_group.uuid}`}
                                  target="_blank"
                                >
                                  {selectedInvoice.company_group.name}
                                </ConditionalLink>
                              </InfoItem>

                              <InfoItem label="Entity">
                                <ConditionalLink
                                  to={`/admin/crm/${selectedInvoice.company_group.uuid}/entity/${selectedInvoice.company_entity.uuid}`}
                                  target="_blank"
                                >
                                  {
                                    selectedInvoice.company_entity
                                      .registered_name
                                  }
                                </ConditionalLink>
                              </InfoItem>
                            </div>
                          </Accordion.Content>
                        </Accordion.Item>
                      )}

                      <Accordion.Item value="service-details">
                        <Accordion.Trigger className="t-text-text-100">
                          Service Details
                        </Accordion.Trigger>
                        <Accordion.Content>
                          <div className="t-grid t-grid-cols-2 t-gap-4">
                            {selectedInvoice.task?.accounting_method && (
                              <InfoItem label="Accounting Method">
                                {
                                  ACCOUNTING_METHODS_MAP[
                                    selectedInvoice.task.accounting_method
                                  ]
                                }
                              </InfoItem>
                            )}

                            <InfoItem label="No.of transactions">
                              {selectedInvoice.task?.no_of_transactions || 0}
                            </InfoItem>

                            <InfoItem label="Started from">
                              {formatDate(selectedInvoice.task?.from_date)}
                            </InfoItem>
                            <InfoItem label="To">
                              {formatDate(selectedInvoice.task?.to_date)}
                            </InfoItem>
                            <InfoItem label="Cash expense">
                              <AmountSuperScript
                                amount={Number(
                                  selectedInvoice.task?.cash_expense || 0
                                )}
                              />
                            </InfoItem>
                            <InfoItem label="Intra-company transfers">
                              <AmountSuperScript
                                amount={Number(
                                  selectedInvoice.task
                                    ?.intra_company_transfers || 0
                                )}
                              />
                            </InfoItem>
                            <InfoItem label="Intra-group transfers">
                              <AmountSuperScript
                                amount={
                                  selectedInvoice.task?.intra_group_transfers ||
                                  0
                                }
                              />
                            </InfoItem>
                            <InfoItem label="Billable Expense Volume">
                              <AmountSuperScript
                                amount={Number(
                                  selectedInvoice.task
                                    ?.monthly_billable_expenses_volume || 0
                                )}
                              />
                            </InfoItem>
                          </div>
                        </Accordion.Content>
                      </Accordion.Item>

                      <Accordion.Item value="invoice-details">
                        <Accordion.Trigger className="t-text-text-100">
                          Invoice Details
                        </Accordion.Trigger>
                        <Accordion.Content className="t-flex t-flex-col t-gap-4">
                          <div>
                            <TextInput
                              label="Invoice title"
                              name="task_short_name"
                            />
                          </div>
                          <div>
                            <InfoItem label="Service linked">
                              <Link
                                to={`/filings/${selectedInvoice.task?.uuid}`}
                              >
                                {selectedInvoice.task?.title}
                              </Link>
                            </InfoItem>
                          </div>

                          <div className="t-flex t-gap-3 t-items-center">
                            <span className="t-text-subtext t-font-medium t-text-text-60">
                              Payment details
                            </span>
                            <div className="t-border-neutral-10 t-border-solid t-border-t t-border-0 t-flex-1" />
                          </div>

                          <div className="t-grid t-grid-cols-2 t-gap-6">
                            <InfoItem label="Amount paid">
                              <AmountSuperScript
                                amount={Number(
                                  selectedInvoice.task
                                    ?.linked_invoice_paid_amount
                                )}
                              />
                            </InfoItem>
                            <InfoItem label="Calculated amount">
                              <CalculatedAmount invoice={selectedInvoice} />
                            </InfoItem>
                          </div>

                          <div className="t-flex t-gap-2 t-items-end t-w-full">
                            <div className="t-flex-1">
                              <PriceInput
                                label="Invoice amount"
                                required
                                name="invoice_amount"
                              />
                            </div>
                            <Button
                              type="button"
                              onClick={() =>
                                setFieldValue(
                                  "invoice_amount",
                                  Number(selectedInvoice.amount)
                                )
                              }
                            >
                              Reset
                            </Button>
                          </div>
                        </Accordion.Content>
                      </Accordion.Item>
                    </Accordion.Root>
                  </Modal.Body>
                  <Modal.FooterButtonGroup>
                    <Modal.Root
                      open={rejectModal.isOpen}
                      onOpenChange={rejectModal.toggle}
                    >
                      <Modal.Trigger asChild>
                        <Button isLoading={isRejecting} disabled={isRejecting}>
                          Reject
                        </Button>
                      </Modal.Trigger>
                      <Modal.Content size="regular">
                        <RejectBody
                          isLoading={isRejecting}
                          invoice={selectedInvoice}
                          onSubmit={() => handleRejectInvoice(selectedInvoice)}
                        />
                      </Modal.Content>
                    </Modal.Root>

                    <Modal.Root
                      open={approveModal.isOpen}
                      onOpenChange={approveModal.toggle}
                    >
                      <Button
                        customType="primary"
                        isLoading={isAccepting}
                        disabled={isAccepting}
                        onClick={async () => {
                          const errors = await validateForm();
                          if (Object.keys(errors).length === 0) {
                            submitForm();
                          }
                        }}
                      >
                        Approve
                      </Button>
                      <Modal.Content size="regular">
                        <ApproveBody
                          invoice={selectedInvoice}
                          isLoading={isAccepting}
                          onSubmit={submitForm}
                        />
                      </Modal.Content>
                    </Modal.Root>
                  </Modal.FooterButtonGroup>
                </>
              )}
            </FormikForm>
          </Modal.Content>
        </Modal.Root>
      )}
    </div>
  );
};
